import {useEffect, useState} from 'react'
import {Dimensions, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {FeedbackBar} from '#/components/capsules/play/FeedbackBar'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  usePlayContentPadding,
  usePlayRuleSnap,
  usePlayRuling,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'
const SECONDS_PER_WORD = 12
const MIN_SECONDS = 30

function formatTime(totalSeconds: number) {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function ck(r: number, c: number) {
  return `${r},${c}`
}

function locateWordInGrid(
  grid: string[][],
  word: string,
): {r: number; c: number}[] | null {
  const rows = grid.length
  const cols = grid[0]?.length ?? 0
  const dirs: [number, number][] = [
    [0, 1],
    [0, -1],
    [1, 0],
    [-1, 0],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ]
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      for (const [dr, dc] of dirs) {
        let ok = true
        const cells: {r: number; c: number}[] = []
        for (let i = 0; i < word.length; i++) {
          const rr = r + dr * i
          const cc = c + dc * i
          if (
            rr < 0 ||
            rr >= rows ||
            cc < 0 ||
            cc >= cols ||
            grid[rr]?.[cc] !== word[i]
          ) {
            ok = false
            break
          }
          cells.push({r: rr, c: cc})
        }
        if (ok) return cells
      }
    }
  }
  return null
}

/** Every cell on the straight line between two grid cells (a learner's swipe). */
function getGridLine(
  r1: number,
  c1: number,
  r2: number,
  c2: number,
): {r: number; c: number}[] | null {
  if (r1 === r2 && c1 === c2) return null
  const dr = r1 === r2 ? 0 : r2 > r1 ? 1 : -1
  const dc = c1 === c2 ? 0 : c2 > c1 ? 1 : -1
  if (r1 !== r2 && c1 !== c2 && Math.abs(r2 - r1) !== Math.abs(c2 - c1))
    return null
  const len = Math.max(Math.abs(r2 - r1), Math.abs(c2 - c1))
  const cells: {r: number; c: number}[] = []
  for (let i = 0; i <= len; i++) cells.push({r: r1 + i * dr, c: c1 + i * dc})
  return cells
}

export function FindWordsSlidePlay({
  slide,
  onAnswer,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(20)
  // On ruled paper the letter grid is drawn *on* the paper, so its squares are
  // the paper's own squares — one rule each. A 34px cell on a 26px ruling makes
  // every second row of letters straddle a line.
  const step = usePlayRuling()
  const snap = usePlayRuleSnap()
  const stackGap = usePlayStackGap(14)
  const onPaper = usePlaySuppressOwnSurface()
  const inkSound = useFlashBookInkSound()

  const grid = slide.findWordsGrid ?? []
  const targets = (slide.findWordsTargets ?? []).map(w => w.toUpperCase())
  const cols = grid[0]?.length ?? 8
  const CELL =
    step > 0
      ? step
      : Math.min(
          34,
          Math.max(
            Math.floor((Dimensions.get('window').width - 40) / cols),
            22,
          ),
        )
  const totalSeconds = Math.max(targets.length * SECONDS_PER_WORD, MIN_SECONDS)

  const [startCell, setStartCell] = useState<{r: number; c: number} | null>(
    null,
  )
  const [foundWords, setFoundWords] = useState<string[]>([])
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set())
  const [submitted, setSubmitted] = useState(false)
  const [timedOut, setTimedOut] = useState(false)
  const [missedCells, setMissedCells] = useState<Set<string>>(new Set())
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setStartCell(null)
    setFoundWords([])
    setFoundCells(new Set())
    setSubmitted(false)
    setTimedOut(false)
    setMissedCells(new Set())
    setSecondsLeft(totalSeconds)
  }

  const endExercise = (reason: 'timeout' | 'skip', currentFound: string[]) => {
    if (submitted) return
    const newMissed = new Set<string>()
    targets.forEach(t => {
      if (currentFound.includes(t)) return
      locateWordInGrid(grid, t)?.forEach(cell =>
        newMissed.add(ck(cell.r, cell.c)),
      )
    })
    setMissedCells(newMissed)
    setTimedOut(true)
    setSubmitted(true)
    const maxPts = slide.points ?? 20
    const pts = Math.round(
      (currentFound.length / Math.max(targets.length, 1)) * maxPts,
    )
    onAnswer({
      isCorrect: currentFound.length === targets.length,
      points: pts,
      maxPoints: maxPts,
      userAnswer: currentFound,
    })
    void reason
  }

  useEffect(() => {
    if (submitted || secondsLeft <= 0) return
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [secondsLeft, submitted])

  useEffect(() => {
    if (secondsLeft === 0 && !submitted) {
      const t = setTimeout(() => endExercise('timeout', foundWords), 0)
      return () => clearTimeout(t)
    }
  }, [secondsLeft]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCell = (r: number, c: number) => {
    if (submitted) return
    // Tracing a word is a marking gesture: the nib lands, and then it travels.
    inkSound.mark()
    if (!startCell) {
      setStartCell({r, c})
      return
    }
    if (startCell.r === r && startCell.c === c) {
      setStartCell(null)
      return
    }
    const line = getGridLine(startCell.r, startCell.c, r, c)
    if (!line) {
      setStartCell({r, c})
      return
    }
    const word = line.map(cell => grid[cell.r]?.[cell.c] ?? '').join('')
    const wordRev = word.split('').reverse().join('')
    const found = targets.find(
      t => !foundWords.includes(t) && (t === word || t === wordRev),
    )
    if (found) {
      const newFC = new Set(foundCells)
      line.forEach(cell => newFC.add(ck(cell.r, cell.c)))
      setFoundCells(newFC)
      const newFW = [...foundWords, found]
      setFoundWords(newFW)
      if (newFW.length >= targets.length) {
        setSubmitted(true)
        onAnswer({
          isCorrect: true,
          points: slide.points ?? 20,
          maxPoints: slide.points ?? 20,
          userAnswer: newFW,
        })
      }
    }
    setStartCell(null)
  }

  if (!grid.length) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
        <Text
          style={{
            color: palette.muted,
            fontSize: 14,
            textAlign: 'center',
          }}>
          {l`Grid not generated — edit the capsule to generate the grid.`}
        </Text>
      </View>
    )
  }

  const urgent = secondsLeft <= 10 && !submitted

  return (
    <PlayScroller
      style={{flex: 1}}
      contentContainerStyle={{padding, gap: stackGap, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {!!slide.question && (
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>
      )}

      {/* Timer + skip */}
      {!submitted && (
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: palette.surfaceAlt,
              borderRadius: 12,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderWidth: 1,
              borderColor: urgent ? palette.negative + '55' : palette.border,
            },
            // The clock is page furniture: two rules tall, with no bar drawn
            // across the lines it is sitting on.
            onPaper && {
              backgroundColor: 'transparent',
              borderWidth: 0,
              borderRadius: 0,
              padding: 0,
              minHeight: snap(36),
            },
          ]}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <Feather
              name="clock"
              size={14}
              color={urgent ? palette.negative : palette.muted}
            />
            <Text
              style={{
                color: urgent ? palette.negative : palette.text,
                fontSize: 14,
                fontWeight: '800',
              }}>
              {formatTime(secondsLeft)}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => endExercise('skip', foundWords)}
            style={[
              {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 20,
                backgroundColor: palette.surface,
                borderWidth: 1,
                borderColor: palette.border,
              },
              onPaper && {
                minHeight: step,
                paddingVertical: 0,
                borderWidth: 0,
                backgroundColor: 'transparent',
              },
            ]}>
            <Feather name="skip-forward" size={12} color={palette.body} />
            <Text
              style={{
                color: palette.body,
                fontSize: 12,
                fontWeight: '700',
              }}>
              {l`Skip`}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Target word badges */}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 8,
          // A second row of badges is a second line of the page.
          rowGap: onPaper ? 0 : 8,
        }}>
        {targets.map(word => {
          const isF = foundWords.includes(word)
          const isMissed = timedOut && !isF
          return (
            <View
              key={word}
              style={[
                {
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 20,
                  borderWidth: 1.5,
                  borderColor: isF
                    ? palette.positive
                    : isMissed
                      ? palette.warning
                      : palette.border,
                  backgroundColor: isF
                    ? palette.positive + '22'
                    : isMissed
                      ? palette.warning + '22'
                      : palette.surface,
                },
                // One line of the page tall, so a wrapped row of word badges is
                // a column of lines of writing.
                onPaper && {
                  minHeight: step,
                  paddingVertical: 0,
                  justifyContent: 'center',
                },
              ]}>
              <Text
                style={{
                  color: isF
                    ? palette.positive
                    : isMissed
                      ? palette.warning
                      : palette.body,
                  fontSize: 13,
                  fontWeight: '700',
                  textDecorationLine: isF ? 'line-through' : 'none',
                }}>
                {word}
              </Text>
            </View>
          )
        })}
      </View>

      {/* Letter grid */}
      <View
        style={{
          alignSelf: 'center',
          borderRadius: onPaper ? 0 : 12,
          overflow: 'hidden',
          // A frame is one pixel per side the ruling does not have.
          borderWidth: onPaper ? 0 : 1,
          borderColor: palette.border,
        }}>
        {grid.map((row, r) => (
          <View key={r} style={{flexDirection: 'row'}}>
            {row.map((letter, c) => {
              const isSC = startCell?.r === r && startCell?.c === c
              const isFC = foundCells.has(ck(r, c))
              const isMC = missedCells.has(ck(r, c))
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={c}
                  onPress={() => handleCell(r, c)}
                  style={{
                    width: CELL,
                    height: CELL,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isFC
                      ? palette.positive + '33'
                      : isMC
                        ? palette.warning + '33'
                        : isSC
                          ? primaryColor + '44'
                          : palette.surface,
                    borderWidth: 0.5,
                    borderColor: palette.border,
                  }}>
                  <Text
                    style={{
                      color: isFC
                        ? palette.positive
                        : isMC
                          ? palette.warning
                          : isSC
                            ? primaryColor
                            : palette.body,
                      fontSize: Math.max(CELL - 12, 10),
                      fontWeight: isFC || isMC ? '800' : '500',
                    }}>
                    {letter}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        ))}
      </View>

      {submitted && (
        <FeedbackBar
          state={
            timedOut ? (foundWords.length > 0 ? 'partial' : 'wrong') : 'correct'
          }
          points={Math.round(
            (foundWords.length / Math.max(targets.length, 1)) *
              (slide.points ?? 20),
          )}
          maxPoints={slide.points ?? 20}
          explanation={
            timedOut
              ? `${foundWords.length}/${targets.length} words found.${slide.explanation ? ' ' + slide.explanation : ''}`
              : slide.explanation
          }
        />
      )}

      {!submitted && (
        <Text
          style={{
            color: palette.muted,
            fontSize: 12,
            textAlign: 'center',
          }}>
          {l`Tap the first letter, then the last letter of the word`}
        </Text>
      )}
    </PlayScroller>
  )
}
